import type { ReactNode } from "react";
import type { TextPair } from "../lib/forms";

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function TextInput(props: { label: string; value: string; onChange: (v: string) => void; hint?: string; placeholder?: string; inputMode?: "numeric" | "text" | "url" }) {
  return (
    <Field label={props.label} hint={props.hint}>
      <input type="text" value={props.value} placeholder={props.placeholder} inputMode={props.inputMode} onChange={(e) => props.onChange(e.target.value)} />
    </Field>
  );
}

export function TextArea(props: { label: string; value: string; onChange: (v: string) => void; rows?: number; hint?: string }) {
  return (
    <Field label={props.label} hint={props.hint}>
      <textarea rows={props.rows ?? 4} value={props.value} onChange={(e) => props.onChange(e.target.value)} />
    </Field>
  );
}

/** Japanese (primary) and English side by side. */
export function LocalizedInput(props: {
  label: string;
  value: TextPair;
  onChange: (v: TextPair) => void;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
}) {
  const Input = props.multiline ? TextArea : TextInput;
  return (
    <fieldset className="localized">
      <legend>
        {props.label}
        {props.required && <span className="required"> *</span>}
      </legend>
      <div className="pair">
        <Input label="日本語" rows={props.rows} value={props.value.ja} onChange={(ja) => props.onChange({ ...props.value, ja })} />
        <Input label="English" rows={props.rows} value={props.value.en} onChange={(en) => props.onChange({ ...props.value, en })} />
      </div>
    </fieldset>
  );
}

export function Select<T extends string>(props: { label: string; value: T; options: readonly { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <Field label={props.label}>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value as T)}>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** Up / down / remove controls for ordered lists. */
export function RowControls(props: { index: number; count: number; onMove: (from: number, to: number) => void; onRemove: () => void }) {
  return (
    <div className="row-controls">
      <button type="button" disabled={props.index === 0} onClick={() => props.onMove(props.index, props.index - 1)} aria-label="上へ">
        ↑
      </button>
      <button type="button" disabled={props.index === props.count - 1} onClick={() => props.onMove(props.index, props.index + 1)} aria-label="下へ">
        ↓
      </button>
      <button type="button" className="danger" onClick={props.onRemove}>
        削除
      </button>
    </div>
  );
}

export function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function ErrorList({ title, errors }: { title: string; errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="notice error" role="alert">
      <strong>{title}</strong>
      <ul>
        {errors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  );
}
